import { getMonth } from "@utils";
import { WORKING_SET_NAME_MAX } from "./workingSets";
import {
  PAYER_TYPE_OPTIONS,
  WATCH_FLAG_OPTIONS,
} from "../../trade-accounts/components/FactCells";

// The New working set builder's fields mirror the Trade Accounts columns: same keys, types and
// value shapes, so a set opens there as the same filters (and the same chips). `buildFields` applies
// the same visibility rules the columns do. Anything not offered can still come in via a saved filter.

export const FIELD_GROUPS = [
  { key: "risk", label: "Risk & behaviour" },
  { key: "ageing", label: "Overdue & ageing" },
  { key: "balances", label: "Balances & credit" },
  { key: "account", label: "Account details" },
];

const normaliseOptions = (values) =>
  (values ?? [])
    .filter((v) => v != null && v !== "")
    .map((v) => (typeof v === "object" ? { value: v.value, label: v.label ?? String(v.value) } : { value: v, label: String(v) }));

const money = (key, title, group) => ({ key, title, group, type: "number", prefix: "£" });

// Fields whose options are fixed in the client. Templates are built from these alone.
const FACT_FIELDS = [
  { key: "watchFlags", title: "Watch flags", group: "risk", type: "enum", options: WATCH_FLAG_OPTIONS },
  { key: "payerTypes", title: "Payer type", group: "risk", type: "enum", options: PAYER_TYPE_OPTIONS },
  { key: "oldestInvoiceDaysPastDue", title: "Days past due", group: "risk", type: "number", suffix: "days" },
  { key: "paymentPerformanceScore", title: "Payment score", group: "risk", type: "number" },
];

const amountFields = () => [
  money("overdue", "Overdue", "ageing"),
  money("totalDue", "Total due", "ageing"),
  money("dueThisMonth", "Due this month", "ageing"),
  money("dueOneMonthAgo", `Due in ${getMonth(-1)}`, "ageing"),
  money("dueTwoMonthsAgo", `Due in ${getMonth(-2)}`, "ageing"),
  money("dueThreeOrMoreMonthsAgo", `Due before ${getMonth(-2)}`, "ageing"),
  money("balance", "All invoices", "balances"),
  money("current", "Due in future months", "balances"),
  money("creditLimit", "Credit limit", "balances"),
  { key: "creditUtilisation", title: "Utilisation", group: "balances", type: "number", suffix: "%" },
  money("unallocated", "Unallocated", "balances"),
];

const STATIC_FIELDS = [...FACT_FIELDS, ...amountFields()];
const staticField = (key) => STATIC_FIELDS.find((f) => f.key === key);

/**
 * Every field the builder offers for this merchant and user. Mirrors the Trade Accounts column
 * rules: ERP-setting toggles hide credit code / category / salesperson / custom properties /
 * financed, a branch-scoped user can't filter by branch, customer facts need AI features, and
 * merchant column-title overrides rename fields. Option-backed fields with no options are dropped.
 */
export const buildFields = ({
  merchant,
  metadata,
  groups = [],
  paymentProfiles = [],
  branches = [],
  isBranchScoped = false,
  showFacts = true,
}) => {
  const erp = merchant?.erpSettings ?? {};
  const overrides = merchant?.customisationSettings?.columnOverrides ?? [];
  const titled = (field) => {
    const match = overrides.find((o) => o.key === `tradeAccounts.${field.key}`);
    return match?.value ? { ...field, title: match.value } : field;
  };
  const optionField = (key, title, values, extra = {}) => ({
    key,
    title,
    group: "account",
    type: "enum",
    options: normaliseOptions(values),
    ...extra,
  });

  const fields = [
    ...(showFacts ? FACT_FIELDS : []),
    ...amountFields(),
    optionField("groups", "Groups", groups.map((g) => ({ value: g.id, label: g.name })), { type: "includeExcludeEnum" }),
    optionField("paymentProfileId", "Payment profile", paymentProfiles.map((p) => ({ value: p.id, label: p.name }))),
    erp.showCreditCode && optionField("creditCode", "Credit code", metadata?.creditCodes),
    erp.showCategory && optionField("categoryCode", "Category", metadata?.categories),
    erp.showSalesperson && optionField("salesPersonCode", "Sales person", metadata?.salespeople),
    !isBranchScoped && optionField("branch", "Branch", branches.map((b) => b.name)),
    erp.showCustomProperty1 &&
      optionField("customProperty1", erp.customProperty1Name ?? "Custom property 1", metadata?.customPropertyOnes),
    erp.showCustomProperty2 &&
      optionField("customProperty2", erp.customProperty2Name ?? "Custom property 2", metadata?.customPropertyTwos),
    erp.showCustomProperty3 &&
      optionField("customProperty3", erp.customProperty3Name ?? "Custom property 3", metadata?.customPropertyThrees),
    erp.showIsFinanced && optionField("isFinanced", "Financed", ["Yes", "No"]),
  ];

  return fields
    .filter(Boolean)
    .filter((f) => f.type === "number" || f.options.length > 0)
    .map(titled);
};

export const fieldFor = (fields, key) => fields.find((f) => f.key === key) ?? null;

export const NUMBER_OPERATORS = [
  { value: "greaterThan", label: "more than" },
  { value: "lessThan", label: "less than" },
  { value: "between", label: "between" },
];

export const INCLUDE_EXCLUDE_MODES = [
  { value: "include", label: "is in" },
  { value: "exclude", label: "is not in" },
];

export const emptyValue = (field) => {
  if (field.type === "enum") return [];
  if (field.type === "includeExcludeEnum") return [{ mode: "include", selected: [] }];
  return { operator: "greaterThan", from: "", to: "" };
};

export const newCondition = (field) => ({
  key: field.key,
  title: field.title,
  type: field.type,
  value: emptyValue(field),
});

const hasNumber = (v) => v !== "" && v != null && Number.isFinite(Number(v));

// A condition only narrows the list once it has a value; half-filled rows are ignored rather than
// sent as a filter that would match everything (or nothing).
export const isComplete = (condition) => {
  const { type, value } = condition;
  if (type === "enum") return Array.isArray(value) && value.length > 0;
  if (type === "includeExcludeEnum") return Array.isArray(value) && value.some((c) => c.selected?.length > 0);
  if (type === "number") {
    if (!value?.operator) return false;
    if (value.operator === "greaterThan") return hasNumber(value.from);
    if (value.operator === "lessThan") return hasNumber(value.to);
    return hasNumber(value.from) && hasNumber(value.to);
  }
  if (type === "date") return !!(value?.from || value?.to);
  if (type === "string") return !!value;
  return false;
};

export const completeFilters = (conditions) => conditions.filter(isComplete);

const fmtNumber = (field, n) => {
  const num = Number(n).toLocaleString("en-GB");
  if (field?.prefix) return `${field.prefix}${num}`;
  if (field?.suffix === "%") return `${num}%`;
  if (field?.suffix) return `${num} ${field.suffix}`;
  return num;
};

const optionLabel = (field, value) => field?.options?.find((o) => o.value === value)?.label ?? String(value);

const listLabels = (labels) => (labels.length <= 2 ? labels.join(" or ") : `${labels[0]} +${labels.length - 1} more`);

// Plain-English summary of a condition, for the suggested name and carried-over chips. Fields
// without their own wording (codes, profiles) lead with the field title so the value has context.
export const describeCondition = (condition, fields = STATIC_FIELDS) => {
  const field = fieldFor(fields, condition.key) ?? staticField(condition.key);
  const title = field?.title ?? condition.title ?? condition.key;
  const { type, value } = condition;
  if (type === "enum" && Array.isArray(value)) {
    const labels = listLabels(value.map((v) => optionLabel(field, v)));
    if (condition.key === "payerTypes") return `${labels} payers`;
    return field?.group === "risk" ? labels : `${title}: ${labels}`;
  }
  if (type === "includeExcludeEnum" && Array.isArray(value)) {
    const include = value.filter((c) => c.mode === "include").flatMap((c) => c.selected ?? []);
    const exclude = value.filter((c) => c.mode === "exclude").flatMap((c) => c.selected ?? []);
    const parts = [];
    if (include.length) parts.push(`In ${listLabels(include.map((v) => optionLabel(field, v)))}`);
    if (exclude.length) parts.push(`Not in ${listLabels(exclude.map((v) => optionLabel(field, v)))}`);
    return parts.join(", ");
  }
  if (type === "number" && value) {
    if (value.operator === "greaterThan") return `${title} over ${fmtNumber(field, value.from)}`;
    if (value.operator === "lessThan") return `${title} under ${fmtNumber(field, value.to)}`;
    return `${title} ${fmtNumber(field, value.from)}–${fmtNumber(field, value.to)}`;
  }
  return title;
};

export const suggestName = (conditions, fields) => {
  const complete = completeFilters(conditions);
  if (complete.length === 0) return "";
  const named = complete
    .slice(0, 2)
    .map((c) => describeCondition(c, fields))
    .join(" · ");
  const name = complete.length > 2 ? `${named} +${complete.length - 2}` : named;
  return name.length > WORKING_SET_NAME_MAX ? `${name.slice(0, WORKING_SET_NAME_MAX - 1)}…` : name;
};

const enumOn = (key, values) => ({ ...newCondition(staticField(key)), value: values });
const over = (key, from) => ({
  ...newCondition(staticField(key)),
  value: { operator: "greaterThan", from: String(from), to: "" },
});

// A cohort's filters narrowed to one of the slices a plan-of-action suggestion targets ("overdue",
// "dpd90", "aged3m" or "flag:{WatchFlag}"). Null when the slice can't be stacked on the cohort exactly:
// a watch flag the cohort doesn't name when it filters on flags (its flags match ANY-of), or a number
// condition the cohort already sets some other way than "greater than".
export const cohortSubsetFilters = (baseFilters, subset) => {
  const narrow = (condition) => {
    const existing = baseFilters.find((f) => f.key === condition.key);
    if (!existing) return [...baseFilters, condition];
    if (condition.type === "enum") {
      const [flag] = condition.value;
      return existing.value?.includes(flag)
        ? baseFilters.map((f) => (f === existing ? { ...f, value: [flag] } : f))
        : null;
    }
    if (existing.value?.operator !== "greaterThan") return null;
    const from = Math.max(Number(existing.value.from) || 0, Number(condition.value.from));
    return baseFilters.map((f) => (f === existing ? { ...f, value: { ...f.value, from: String(from) } } : f));
  };

  if (subset?.startsWith("flag:")) return narrow(enumOn("watchFlags", [subset.slice("flag:".length)]));
  if (subset === "dpd90") return narrow(over("oldestInvoiceDaysPastDue", 90));
  if (subset === "aged3m") return narrow(over("dueThreeOrMoreMonthsAgo", 0));
  if (subset === "overdue") return narrow(over("overdue", 0));
  return null;
};

// Whether a Trade Accounts row falls in a plan step's slice: the row-side reading of cohortSubsetFilters.
export const cohortSubsetMatches = (row, subset) => {
  if (subset?.startsWith("flag:")) return (row.watchFlags ?? []).includes(subset.slice("flag:".length));
  if (subset === "dpd90") return (row.oldestInvoiceDaysPastDue ?? 0) > 90;
  if (subset === "aged3m") return (row.dueThreeOrMoreMonthsAgo ?? 0) > 0;
  if (subset === "overdue") return (row.overdue ?? 0) > 0;
  return false;
};

// One-click starting points, built only from what Trade Accounts can already filter on.
export const WORKING_SET_TEMPLATES = [
  {
    id: "over-credit-limit",
    name: "Over credit limit",
    blurb: "Balance above their limit",
    icon: "creditCard",
    colour: "orange",
    filters: [enumOn("watchFlags", ["OverCreditLimit"])],
  },
  {
    id: "dpd-60",
    name: "60+ days past due",
    blurb: "Oldest invoice over 60 days late",
    icon: "clock",
    colour: "amber",
    filters: [over("oldestInvoiceDaysPastDue", 60)],
  },
  {
    id: "aged-debt",
    name: "Overdue 3+ months",
    blurb: "Money owed from 3+ months ago",
    icon: "calendar",
    colour: "red",
    filters: [over("dueThreeOrMoreMonthsAgo", 0)],
  },
  {
    id: "at-risk",
    name: "At risk",
    blurb: "Payment behaviour deteriorating",
    icon: "warning",
    colour: "red",
    filters: [enumOn("watchFlags", ["AtRisk"])],
  },
  {
    id: "paying-partial",
    name: "Paying partially",
    blurb: "Settling invoices in part",
    icon: "banknotes",
    colour: "amber",
    filters: [enumOn("watchFlags", ["PayingPartial"])],
  },
  {
    id: "dormant-owing",
    name: "Dormant with balance",
    blurb: "Not trading, still owing",
    icon: "moon",
    colour: "indigo",
    filters: [enumOn("payerTypes", ["Dormant"]), over("balance", 0)],
  },
  {
    id: "late-payers",
    name: "Very late payers",
    blurb: "Severely late or unpredictable",
    icon: "hand",
    colour: "red",
    filters: [enumOn("payerTypes", ["SeverelyLate", "Unpredictable"])],
  },
];
